import { useSessionStorage } from '@mantine/hooks';
import {
  httpClient,
  basicAxiosInstance,
  tryRenewAuth,
} from 'providers/clients/httpClient';
import {
  createContext,
  memo,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import BloxLoadingSplash from '../../components/generic/BloxLoadingSplash';
import ErrorAlerts, { ErrorDetails } from '../../components/generic/ErrorAlerts';
import NoCosmosPermissions from 'components/NoAccess/NoCosmosPermissions';
import {
  ApiErrorResponse,
  GetUserMeData,
  GetUserMeGroupsData,
  SessionConfigFetchResult,
} from '../../utility/types/dataProvider';
import {
  getAuthTokenViaStorage,
  getAuthTokenViaUser,
  getUserDefaultSite,
  hardReloadClearAuth,
  isJwtExpired,
  AUTH_RAND_RF_TIME,
  useAuthStoreZustand,
} from './CosmosAuthEnvContext';
import { usersProvider } from './extensions/Users';
import { flushSync } from 'react-dom';
import { hasCosmosPrivilege } from 'utility/privileges';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

// These functions will go through the standard httpclient to fetch
// So they will need activeSiteZustand, apiurl, and authtoken
// already set in the zustand store before you call them.
// The dataProvider is not available yet here.
const { getActiveUserGroups, getUserMe } = usersProvider;

//----------------------------------------------------------------------
// Types and Consts
//----------------------------------------------------------------------

export type TSessionConfigData = SessionConfigFetchResult['data'];
export type CosmosContextType = {
  changeActiveSiteAsync: (site: string, path?: string) => Promise<string>;
  clearInvalidDomain: () => void;
  invalidDomain: string;
  /** The currently 'active' site/domain. */
  activeSite: string;
  /** /core/session/config - (user, site_admin, permissions, etc) */
  sessionConfig?: TSessionConfigData;
  /**  /user/me/ - (uuid, first_name, etc)  */
  userInfo?: GetUserMeData;
  /** /user/me/groups - fetched data  */
  userGroups?: GetUserMeGroupsData;
  refetchCosmosUserGroups: UseQueryResult<
    GetUserMeGroupsData,
    ApiErrorResponse
  >['refetch'];
};

type TSiteSwitchArgs = { site: string; path?: string };

// Initial state - certain other values for context are derived from this state
const baseCosmosState: CosmosStateType = {
  renderReady: false, // prevent no permissions flicker
  initStatus: undefined, // Track init status for effect
  activeSite: '',
  sessionConfig: undefined,
  userInfo: undefined,
  userGroups: undefined,
};

type CosmosStateType = {
  renderReady: boolean;
  activeSite: string;
  initStatus: 'success' | 'invalid' | 'failed' | undefined;
  sessionConfig?: TSessionConfigData;
  userInfo?: GetUserMeData;
  userGroups?: GetUserMeGroupsData;
  initError?: ApiErrorResponse;
};

/**
 * Custom Type for the initialSessionConfig useQuery
 * Status:
 * - success: the domain we tried was valid and we got the data
 * - invalid: the domain we tried was invalid and you need to fallback to oidc site
 * - failed: the oidc fallback site failed to initialize the app, cosmos is unable to init
 */
type InitialStateUpdate = {
  initStatus: 'success' | 'invalid' | 'failed'; // track attempt status for effect
  badSite: string; // if bad status return the site that failed/invalid for invalidDomain storage
  activeSite: string; // empty string until success
  sessionConfig?: TSessionConfigData; // undefined until success
  initError?: ApiErrorResponse; // maybe api error details to display
};

type InitCosmosQueryFnParams = {
  domain?: string;
  apiUrl?: string;
  isFinalAttempt?: boolean; // trying jwt fallback after domain
};

//----------------------------------------------------------------------
// Helper Functions
//----------------------------------------------------------------------
export function getAddressBarDomain(pathname: string) {
  return pathname?.split('/')[1]?.toLowerCase()?.trim();
}

function checkCosmosAccess(sessionConfig?: TSessionConfigData, userInfo?: GetUserMeData) {
  // No session or user info, can't determine access yet.
  if (!sessionConfig || !userInfo) {
    return false;
  }
  // Staff always allowed, otherwise must have cosmos privileges.
  const hasCosmos = hasCosmosPrivilege(sessionConfig);
  const isStaffUser = Boolean(userInfo?.account_type === 'staff');
  return isStaffUser || hasCosmos;
}

//----------------------------------------------------------------------
// Query Functions
//----------------------------------------------------------------------

/**
 * Get users session config
 *
 * It will call using the basicAxiosInstance on httpClient
 * rather than the mainAxiosInstance on httpClient.
 * as the mainAxiosInstance would need certain states
 * in storage before it can be used with the httpClient.
 *
 * This is required for the initial call, as the app must determine if the url
 * in the address bar is valid for the user. If it fails, we assume that the
 * user is not authorized to the site and then retry with the x-blox-domain
 * set to their jwt "profile.bloxcms.default_site". If that fallback jwt
 * default_site fails or is missing, the app cannot initialize.
 *
 */

type GetSessionConfigBasicParams = {
  activeSite?: string;
  apiUrl?: string;
};

async function getSessionConfigBasic({
  activeSite,
  apiUrl,
}: GetSessionConfigBasicParams): Promise<SessionConfigFetchResult> {
  try {
    if (!activeSite || !apiUrl) {
      throw new Error('Missing required parameters.');
    }

    const fullUrl = `${apiUrl}/core/session/config/`;

    // This is a rare edge case as the user should
    // have just signed in moments ago, but in case
    // somehow this fn is invoked with a stale token
    // eg if the browser was offline or idle for a long time
    // with no reauth and the page refreshed or reloaded...

    // Check to see if the token is missing or expired.
    // If expired then try to refresh silently...
    let authToken = await getAuthTokenViaStorage();

    if (!authToken) {
      console.error('[getSessionConfigBasic] No auth token found in storage.');
      throw new Error('No auth token found in storage.');
    }

    // Check if the token is expired or near expiry and should be refreshed
    const shouldRefreshToken = isJwtExpired(authToken, AUTH_RAND_RF_TIME);

    // Await the token refresh process using the global lock promise.
    if (shouldRefreshToken) {
      try {
        authToken = await tryRenewAuth();
      } catch (error) {
        console.error('[getSessionConfigBasic] Token Refresh Error:', error);
        throw new Error('Error during token refresh.');
      }
    }

    // note: basic axios instance for this httpclient
    const { data, status } = await httpClient<SessionConfigFetchResult>(
      {
        url: fullUrl,
        headers: {
          Authorization: authToken,
          'X-Blox-Domain': activeSite,
          'X-Request-Id': uuidv4(),
        },
      },
      basicAxiosInstance,
    );

    const result: SessionConfigFetchResult = {
      status: data?.status || status,
      data: data?.data,
    };

    return result;
  } catch (error) {
    console.error('[getSessionConfigBasic]', error);
    throw error;
  }
}

/**
 * Intialize Cosmos
 * - GET /core/session/config/
 * - Determine if fallback / final attempt.
 * - Return structured cosmos state update.
 */
async function initCosmosQueryFn({
  domain,
  isFinalAttempt,
  apiUrl,
}: InitCosmosQueryFnParams): Promise<InitialStateUpdate> {
  if (!domain || !apiUrl) {
    throw new Error('[initCosmosQueryFn] Missing required parameters.');
  }

  try {
    // Try using the domain that was passed to the function
    const sessionConfigResult = await getSessionConfigBasic({
      activeSite: domain,
      apiUrl: apiUrl,
    });

    // If successful the domain we tried was valid.
    const successState: InitialStateUpdate = {
      initStatus: 'success',
      sessionConfig: sessionConfigResult?.data,
      activeSite: String(domain),
      badSite: '',
    };

    return successState;
  } catch (error) {
    /**
     * If the fetch was unsuccesful - return an "invalid" state.
     * Check if the flag "isFinalAttempt" was true, this indicates we
     * we are trying the fallback jwt oidc domain, or have exhausted
     * all fallback options to determine a domain to try. The app
     * cannot initialize, attach api error details for ui display,
     */
    const failedState: InitialStateUpdate = {
      initStatus: isFinalAttempt ? 'failed' : 'invalid',
      badSite: String(domain),
      activeSite: '',
      sessionConfig: undefined,
      initError: error as ApiErrorResponse,
    };

    return failedState;
  }
}

//----------------------------------------------------------------------
// CosmosContext Provider and Hooks
//----------------------------------------------------------------------
export const CosmosContext = createContext<CosmosContextType | undefined>(undefined);

export const useCosmos = () => {
  const context = useContext(CosmosContext);
  if (!context) {
    throw new Error('useCosmos must be used within a CosmosProvider!');
  }
  return context;
};

export const CosmosProvider = ({ children }: PropsWithChildren) => {
  const { envState, currentUser, activeSiteZustand, setActiveSiteZustand } =
    useAuthStoreZustand();
  const apiUrl = envState?.apiUrl;
  const { t } = useTranslation();
  const strInitializing = t('blox.general.initializing');
  const navigate = useNavigate();
  const location = useLocation();
  const { queryClient } = useCosmosQueryClient();
  const [cosmosState, setCosmosState] = useState<CosmosStateType>(baseCosmosState);

  // session storage for invalid domain as app must nav to reconfigure if it was invalid
  const [invalidDomain, setInvalidDomain] = useSessionStorage<string>({
    key: 'invalidDomain',
    defaultValue: '',
  });

  // Check if the user has access to cosmos
  const canAccessCosmos = useMemo(() => {
    return checkCosmosAccess(cosmosState?.sessionConfig, cosmosState?.userInfo);
  }, [cosmosState?.sessionConfig, cosmosState?.userInfo]);

  // Setup variables for init process
  const authToken = getAuthTokenViaUser(currentUser);
  const addressBarDomain = getAddressBarDomain(location.pathname);
  const authBloxCMSDefaultSite = getUserDefaultSite(currentUser);

  // Note: In theory the "bloxcms.default_site" should always exist
  // but in practice there are cases of this being null/empty.
  // eg: CMSADMIN-2375 - deleted admin acc w/ no other sites

  // Domain we will use for our init query on app load and if the user switches sites.
  // Use address bar domain or fallback to the oidc default site if bar is empty
  const [domainForQuery, setDomainForQuery] = useState<string>(() => {
    const initialDomain = addressBarDomain || authBloxCMSDefaultSite;
    return initialDomain;
  });

  const clearInvalidDomain = useCallback(() => {
    setInvalidDomain('');
  }, [setInvalidDomain]);

  // Mutation - Swap to the domain string provided.
  const { mutateAsync: changeActiveSiteAsync, status: mutateSiteStatus } = useMutation({
    mutationFn: async ({ site, path }: TSiteSwitchArgs) => {
      // Wait to make this a real async fn and allow states time to reconfig
      // basically allows this fn to go into "pending" state for other checks...
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Navigate to the new site requested with the path if provided.
      const normalizedSite = site.toLowerCase();
      const targetPath = path ? `/${normalizedSite}${path}` : `/${normalizedSite}`;

      navigate(targetPath, {
        replace: false,
        state: {
          fromSite: addressBarDomain?.toLowerCase(),
          newSite: normalizedSite,
        },
      });

      // Return string for onSettled to set the new active site init refetch.
      return normalizedSite;
    },
    retry: false,
    // onMutate docs: function will fire before the mutation function is fired
    // and is passed the same variables the mutation function would receive
    onMutate: async (variables: TSiteSwitchArgs) => {
      // On swapped site - Clear/reset/invalidate any errors,
      // queries, states as we are about to refetch / reinitialize.

      // Empty domainForQuery to disable refetch while switching
      flushSync(() => {
        setDomainForQuery('');
      });

      try {
        /**
         * Invalidate all React Query v5 queries
         */

        // Cancel any outgoing queries
        await queryClient.cancelQueries(
          {
            type: 'all',
          },
          {
            revert: false,
            silent: true,
          },
        );
        // Invalidate all and without refetching
        await queryClient.invalidateQueries(
          {
            type: 'all',
            refetchType: 'none',
          },
          {
            throwOnError: false,
            cancelRefetch: true,
          },
        );

        // Remove all queries from the cache
        queryClient.removeQueries({
          type: 'all',
        });
      } catch (error) {
        console.error('[CosmosContext - onMutate] Error:', error);
      }

      // Important: ensure queryClient cache is cleared as app will reconfigure / reload
      queryClient.clear();

      // Clear any invalid domain / storages... reinit will set new values if needed.
      setInvalidDomain('');

      // Also reset zustand active site to be safe
      setActiveSiteZustand('');

      // Reset the cosmos state to initial state
      setCosmosState(baseCosmosState);

      // Now go to the mutationFn...
      return variables.site;
    },
    onSettled: async (result: string | undefined) => {
      // mutationFn returns newActiveSite string so set it as the new
      // domainForQuery once routing is done and to re-enable the init query.
      if (!result) {
        // Best to hard reset to root if things error here.
        console.error('[CosmosContext - onSettled] falsy newActiveSite:', result);
        hardReloadClearAuth({ type: 'origin' });
      } else {
        const cleanResult = result?.toLowerCase()?.trim() || '';
        setDomainForQuery(cleanResult);
      }
    },
    onError: (error: Error) => {
      // Should not error as the mutations just a navigate call, logging anyway for posterity.
      console.error('[CosmosContext - Mutation] onError...', error);
    },
  });

  // Wrapper to update domain context and navigate to the corresponding route.
  const changeSiteFn = useCallback(
    (site: string, path?: string) => changeActiveSiteAsync({ site, path }),
    [changeActiveSiteAsync],
  );

  /**
   * Enable init query when required values are available
   * If active site is set we already ran the fetch.
   */
  const initQueryEnabled = Boolean(
    domainForQuery && // must have domain set to query
    !cosmosState.activeSite && // state not already set
    !activeSiteZustand && // also ensure zustand was cleared
    currentUser &&
    apiUrl &&
    authToken,
  );

  const {
    data: initCosmosQueryData,
    error: initCosmosQueryError,
    status: initCosmosQueryStatus,
  } = useQuery<InitialStateUpdate, ApiErrorResponse>({
    queryKey: [`site:${domainForQuery}`, 'CosmosProvider', 'getSessionConfigBasic'],
    queryFn: async () => {
      /**
       * Final attempt:
       * The domainForQuery is the same as the jwt default site or
       * there is no "bloxcms.default_site" in the jwt to fallback.
       */
      const isTryingDefault = domainForQuery === authBloxCMSDefaultSite;
      const isFinalAttempt = isTryingDefault || !authBloxCMSDefaultSite;
      const result = await initCosmosQueryFn({
        domain: domainForQuery,
        apiUrl: apiUrl,
        isFinalAttempt: isFinalAttempt,
      });
      return result;
    },
    enabled: initQueryEnabled,
    gcTime: 0, // no bg/auto refetch
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  /**
   * Note: rare edge case where no domain exists in the
   * address bar and no fallback jwt default_site exists.
   * In this case the init query will not enable / run.
   */
  useEffect(() => {
    // Skip if initial query already ran.
    // The other effect below handles updates.
    if (initCosmosQueryData) {
      return;
    }

    const noValidFallback = !addressBarDomain && !authBloxCMSDefaultSite;
    if (noValidFallback) {
      setCosmosState((prevValues) => ({
        ...prevValues,
        activeSite: '',
        sessionConfig: undefined,
        initStatus: 'failed',
        initError: {
          status: 'error',
          message: 'No domain was specified, and token is missing default site.',
          details: 'Please contact your administrator.',
        },
      }));
    }
  }, [addressBarDomain, authBloxCMSDefaultSite, initCosmosQueryData]);

  /**
   * Sync to state initCosmosQuery once it's available or changes.
   */
  useEffect(() => {
    // No data yet - likely query is disabled or loading.
    if (!initCosmosQueryData) {
      return;
    }

    const initStatus = initCosmosQueryData?.initStatus;
    const activeSite = initCosmosQueryData?.activeSite;
    const sessionConfig = initCosmosQueryData?.sessionConfig;
    const badSite = initCosmosQueryData?.badSite;
    const initError = initCosmosQueryData?.initError;

    // Fetch was valid - update context with new/initial data.
    if (initStatus === 'success') {
      setCosmosState((prevValues) => ({
        ...prevValues,
        activeSite: activeSite,
        sessionConfig: sessionConfig,
        initStatus: initStatus,
        initError: initError,
      }));
      // Copy to zustand for use in httpclient
      setActiveSiteZustand(activeSite);
      // domainForQuery could have fallbacked to use the OIDC site, for example if
      // there was an empty string in the addressBarDomain and we fallback to oidc default site
      // so if the determined active site is not the addressBarDomain then
      // navigate to the site that we used to initialize the app to correct the address bar
      if (addressBarDomain !== activeSite) {
        navigate(`/${activeSite}`, {
          replace: true,
          state: {
            fromSite: addressBarDomain,
            newSite: activeSite,
          },
        });
      }
    }

    // Update the cosmos state - invalid means we need to try again
    // using the oidc fallback site... likely the user typed in a domain that
    // was invalid so we need to re-run the initCosmosQuery with the oidc site
    // call navigate with the oidc site to change queryKey and refetch
    if (initStatus === 'invalid') {
      setCosmosState((prevValues) => ({
        ...prevValues,
        activeSite: '',
        sessionConfig: undefined,
        initStatus: initStatus,
        initError: initError,
      }));
      setActiveSiteZustand(''); // Match cosmos state
      setInvalidDomain(badSite); // Store the bad site for error display
      setDomainForQuery(authBloxCMSDefaultSite); // Retry with oidc site
    }

    // Failed means even the fallback oidc site has failed
    // and cosmos cant proceed with initialization at all.
    if (initStatus === 'failed') {
      console.error('[CosmosProvider] - Failed to initialize domain:', initError);
      setCosmosState((prevValues) => ({
        ...prevValues,
        activeSite: '',
        sessionConfig: undefined,
        initStatus: initStatus,
        initError: initError,
      }));
      setActiveSiteZustand('');
      setInvalidDomain(badSite);
    }
  }, [
    queryClient,
    setActiveSiteZustand,
    initCosmosQueryData,
    addressBarDomain,
    setInvalidDomain,
    authBloxCMSDefaultSite,
    navigate,
  ]);

  /**
   *  RQ v5 note: v5 deprecated keepPreviousData in favor of placeholderData
   *  but placeholderData forces the query to always be in "success" state
   *  do not use placeholderData as can cause flicker of no cosmos perms
   *  https://tanstack.com/query/v5/docs/framework/react/guides/migrating-to-v5#removed-keeppreviousdata-in-favor-of-placeholderdata-identity-function
   */

  // Enable post-init queries once the activeSite is set and initStatus is success
  const enablePostInitQueries = Boolean(
    apiUrl &&
    activeSiteZustand &&
    cosmosState?.activeSite &&
    cosmosState?.initStatus === 'success',
  );

  // Get current user info from /user/me/
  const {
    data: userMeQueryData,
    error: userMeQueryError,
    status: userMeQueryStatus,
  } = useQuery<GetUserMeData, ApiErrorResponse>({
    queryKey: [`site:${cosmosState?.activeSite}`, 'CosmosProvider', 'getUserMe'],
    queryFn: async () => {
      const userInfo = await getUserMe();
      const userData: GetUserMeData = { ...userInfo?.data };
      return userData;
    },
    enabled: enablePostInitQueries,
    gcTime: 0, // no bg/auto refetch
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Sync to state userMeData once it's available or changes.
  useEffect(() => {
    if (userMeQueryData) {
      setCosmosState((prevValues) => ({
        ...prevValues,
        userInfo: userMeQueryData,
      }));
    }
  }, [userMeQueryData]);

  // Get current user groups string[] from /user/me/groups
  const {
    data: userGroupsQueryData,
    error: userGroupsQueryError,
    status: userGroupsQueryStatus,
    refetch: refetchCosmosUserGroups,
  } = useQuery<GetUserMeGroupsData, ApiErrorResponse>({
    queryKey: [
      `site:${cosmosState?.activeSite}`,
      'CosmosProvider',
      'getActiveUserGroups',
    ],
    queryFn: async () => {
      const userGroups = await getActiveUserGroups();
      const groupsData: GetUserMeGroupsData = userGroups?.data;
      return groupsData;
    },
    enabled: enablePostInitQueries,
    gcTime: 0, // no bg/auto refetch
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Sync to state userGroupsQueryData once it's available or changes.
  useEffect(() => {
    if (userGroupsQueryData) {
      setCosmosState((prevValues) => ({
        ...prevValues,
        userGroups: userGroupsQueryData,
      }));
    }
  }, [userGroupsQueryData]);

  // Context value
  const contextValue = useMemo(() => {
    return {
      invalidDomain: invalidDomain,
      activeSite: cosmosState?.activeSite,
      sessionConfig: cosmosState?.sessionConfig,
      userInfo: cosmosState?.userInfo,
      userGroups: cosmosState?.userGroups,
      refetchCosmosUserGroups: refetchCosmosUserGroups,
      changeActiveSiteAsync: changeSiteFn,
      clearInvalidDomain: clearInvalidDomain,
    };
  }, [
    cosmosState,
    changeSiteFn,
    invalidDomain,
    clearInvalidDomain,
    refetchCosmosUserGroups,
  ]);

  /**
   * ---------------------------------------------
   * Rendering / Returns
   * ---------------------------------------------
   */

  // Check if all queries are successful and set cosmosState.ready to render
  // as to prevent no permissions flicker while loading/checking.
  useEffect(() => {
    const allReady = Boolean(
      initCosmosQueryStatus === 'success' &&
      userMeQueryStatus === 'success' &&
      userGroupsQueryStatus === 'success' &&
      mutateSiteStatus !== 'pending',
    );
    // All queries are done and ready to render
    if (allReady && !cosmosState.renderReady) {
      setCosmosState((prevValues) => ({
        ...prevValues,
        renderReady: true,
      }));
    }
    // Unready if these were ready and they became not ready
    if (!allReady && cosmosState.renderReady) {
      setCosmosState((prevValues) => ({
        ...prevValues,
        renderReady: false,
      }));
    }
  }, [
    cosmosState,
    initCosmosQueryStatus,
    mutateSiteStatus,
    userMeQueryStatus,
    userGroupsQueryStatus,
  ]);

  /**
   * Check for errors and render error if found.
   */
  const anyErrors = Boolean(
    initCosmosQueryError ||
    userMeQueryError ||
    userGroupsQueryError ||
    cosmosState.initStatus === 'failed',
  );

  if (anyErrors) {
    // Collect errors for display in the component
    // If state was failed, check for init error.
    const errorResults: ErrorDetails[] = [
      initCosmosQueryError,
      userMeQueryError,
      userGroupsQueryError,
      cosmosState?.initError,
    ]
      .filter(Boolean)
      .map((error) => {
        return {
          message: error?.message,
          status: error?.status,
          fullDetails: error,
        };
      });

    return (
      <ErrorAlerts
        severity="error"
        headerText="Application failed to initialize."
        subHeaderText="Please try clearing the browser cache and try again."
        buttonText="Reload"
        errors={errorResults}
        showDetails={true}
        showDivider={true}
        showSeverity={false}
        buttonOnClick={() => {
          // Best to try a hard reset if things fail this early.
          hardReloadClearAuth({ type: 'origin' });
        }}
      />
    );
  }

  // Running initialization queries and state setup
  if (!cosmosState.renderReady || mutateSiteStatus === 'pending') {
    return <BloxLoadingSplash message={strInitializing} />;
  }

  // Does not have access to cosmos
  if (!canAccessCosmos) {
    return (
      <NoCosmosPermissions
        activeSite={cosmosState?.activeSite}
        changeActiveSiteAsync={changeSiteFn}
      />
    );
  }

  // You can see this flash when we hit the back/forward button after changing sites
  // beacuse we need config fetched first in order to determine the the "route"
  // for cosmos to load, but the app was already mounted with activeSite/<routes>/
  // so this fallback should show a loader for brief the duration of a site switch
  // and if it takes too long, it will log a warning for us to investigate.
  if (cosmosState?.activeSite && addressBarDomain !== cosmosState?.activeSite) {
    return (
      <RouteWithTimeoutWarning
        activeSite={cosmosState?.activeSite}
        addressBarDomain={addressBarDomain}
        oidcDefaultSite={authBloxCMSDefaultSite}
      />
    );
  }

  return <CosmosContext.Provider value={contextValue}>{children}</CosmosContext.Provider>;
};

type RouteWithTimeoutWarningProps = {
  activeSite: string;
  addressBarDomain: string;
  oidcDefaultSite: string;
};

const RouteWithTimeoutWarning = memo(
  ({ activeSite, addressBarDomain, oidcDefaultSite }: RouteWithTimeoutWarningProps) => {
    const [isTimedOut, setIsTimedOut] = useState(false);

    // If nothing matches for 30 seconds, log and timeout.
    useEffect(() => {
      if (isTimedOut) return;
      const timeout = setTimeout(() => {
        setIsTimedOut(true);
        console.error(
          '[RouteWithTimeoutWarning] No route was matched for the greater than 10 seconds!\n',
          'Logging information for investigation:',
          '\nactiveSite:',
          activeSite,
          '\naddressBarDomain:',
          addressBarDomain,
          '\noidcDefaultSite:',
          oidcDefaultSite,
        );
      }, 30000);

      // Cleanup timeout on unmount
      return () => {
        clearTimeout(timeout);
      };
    }, [isTimedOut, setIsTimedOut, activeSite, addressBarDomain, oidcDefaultSite]);

    if (isTimedOut) {
      return (
        <ErrorAlerts
          headerText="Error"
          severity="error"
          buttonText="Reload"
          buttonOnClick={() => {
            // Best to offer a hard reset if things fail at this stage
            hardReloadClearAuth({ type: 'origin' });
          }}
          errors={[
            {
              status: `Sorry, we were unable to load the application.`,
              message: `Please try clearing your browser cache and reloading the page.`,
            },
          ]}
        />
      );
    }

    // Show the loading splash while we wait for the site swithch to complate
    // and give react-router a path to render in the meantime fixing the
    // "no route matched" warning from react-router mid back/forward navigation
    // https://github.com/remix-run/react-router/discussions/10851
    return (
      <Routes>
        <Route
          path="*"
          element={<BloxLoadingSplash message="Loading sites..." />}
        />
      </Routes>
    );
  },
);

RouteWithTimeoutWarning.displayName = 'LoadingWithTimeout';
